import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react'
import {
  BrowserRouter as Router,
  useHistory,
  useLocation,
} from 'react-router-dom'
import OrderStore from '../stores/OrderStore'
import { observer } from 'mobx-react-lite'
import AuthStore from '../stores/AuthStore'
import SellerStore from '../stores/SellerStore'
import ItemStore from '../stores/ItemStore'
import 'react-pro-sidebar/dist/css/styles.css'
import Header from '../components/Header'
import socket from '../utils/socket'
import backendApis from '../utils/backendApis'
import Sidebar from '../components/Sidebar'
import Log from '../utils/log'

import AdminRouter from '../components/atoms/adminRouter'
import Modal from 'react-modal'
import CancelPreShippingOrderModal from './CancelPreShippingOrderModal'
import CancelOrderModal from './CancelOrderModal'
import WithdrawCancelingPreShippingOrderModal from './WithdrawCancelingPreShippingOrderModal'

import newbieGuideStage from '../data/newbieGuideStage'
import BusinessRegistrationNumberModal from '../components/atoms/businessRegistrationNumberModal'
import AdQuest from '../components/atoms/adQuest'
import TimedealQuest from '../components/atoms/timedealQuest'
import SellerQuest from '../utils/sellerQuest'
import PriceQuest from '../components/atoms/priceQuest'
import BeautyQuest from '../components/atoms/BeautyQuest'
import ProgressDisplay from '../components/molecules/progressDisplay'
import RevokeRefundRequestModal from './RevokeRefundRequestModal'
import * as ChannelService from '@channel.io/channel-web-sdk-loader'
import BusinessRegistrationModal from './Modals/BusinessRegistrationModal'

const AdminScreen = observer(() => {
  const location = useLocation()
  const history = useHistory()
  
  // 초기화 관련 상태
  const [initializationComplete, setInitializationComplete] = useState(false)
  const [isSellerInfoPrepared, setIsSellerInfoPrepared] = useState(false)
  const initializationRef = useRef(false)
  const channelTalkInitializedRef = useRef(false)
  
  // 기존 상태들
  const [isAgreeToTnCModalOpen, setIsAgreeToTnCModalOpen] = useState(false)
  const [timerCheck, setTimerCheck] = useState(false)
  const [openGuideModal, setOpenGuideModal] = useState(true)
  const [openPriceQuestModal, setOpenPriceQuestModal] = useState(true)
  const [openBeautyQuestModal, setOpenBeautyQuestModal] = useState(true)
  const [openAdQuestModal, setOpenAdQuestModal] = useState(true)
  const [openTimedealQuestModal, setOpenTimedealQuestModal] = useState(true)
  const [showQuest, setShowQuest] = useState(true)

  // 페이지 진입 로그 (location 변경 시에만)
  useEffect(() => {
    Log.event('entering_screen', location.pathname)
  }, [location.pathname])

  // Quest 조건들을 useMemo로 최적화
  const questClearInfo = useMemo(() => {
    if (!SellerStore.sellerInfo || !Object.keys(SellerStore.sellerInfo).length) {
      return {}
    }

    const newbieGuideClearCondition = !SellerStore.sellerInfo.tutorialInfo?.filter(
      (e) => e.stageName === newbieGuideStage.reward && e.cleared === true,
    ).length > 0
    
    return {
      adQuestClearCondition: SellerQuest.rewardChecker('adQuest'),
      timedealQuestClearCondition: SellerQuest.rewardChecker('timedealQuest'),
      priceQuestClearCondition: SellerQuest.rewardChecker('priceQuest'),
      beautyQuestClearCondition: SellerQuest.rewardChecker('beautyQuest'),
      newbieGuideClearCondition,
    }
  }, [SellerStore.sellerInfo])

  // Channel Talk 초기화 (loadSellerInfo 호출 제거)
  const channelTalkBoot = useCallback(() => {
    // 이미 초기화되었거나 sellerInfo가 없으면 return
    if (channelTalkInitializedRef.current || !SellerStore.sellerInfo?._id) {
      return
    }

    try {
      const phoneNumber =
        SellerStore.sellerInfo.phoneNumber?.startsWith('010') &&
        SellerStore.sellerInfo.phoneNumber?.length === 11
          ? SellerStore.sellerInfo.phoneNumber
          : ''
          
      ChannelService.loadScript()
      ChannelService.boot({
        pluginKey: '4d7d756c-2816-4b29-88c0-21b3b60b5cab',
        memberId: SellerStore.sellerInfo._id,
        profile: {
          name: SellerStore.sellerInfo.sellerDisplayName,
          sellerName: SellerStore.sellerInfo.sellerName,
          mobileNumber: phoneNumber,
          email: SellerStore.sellerInfo.email,
          contactNumber: SellerStore.sellerInfo.contactNumber,
          landlineNumber: SellerStore.sellerInfo.contactNumber,
        },
      })
      
      channelTalkInitializedRef.current = true
      console.log('Channel Talk initialized successfully')
    } catch (error) {
      console.error('Channel Talk initialization failed:', error)
    }
  }, []) // 의존성 제거로 재생성 방지

  // Quest Handler 컴포넌트를 useCallback으로 최적화
  const QuestHandler = useCallback(({ questClearInfo }) => {
    if (!showQuest || !Object.keys(questClearInfo).length) return null
    if (!questClearInfo?.priceQuestClearCondition)
      return (
        <PriceQuest
          questInfo={SellerStore?.sellerInfo?.questInfo?.priceQuest}
          openModal={openPriceQuestModal}
          setOpenModal={setOpenPriceQuestModal}
          setShowQuest={setShowQuest}
        />
      )
    if (!questClearInfo?.adQuestClearCondition)
      return (
        <AdQuest
          questInfo={SellerStore?.sellerInfo?.questInfo?.adQuest}
          openModal={openAdQuestModal}
          setOpenModal={setOpenAdQuestModal}
          setShowQuest={setShowQuest}
        />
      )
    if (!questClearInfo?.timedealQuestClearCondition)
      return (
        <TimedealQuest
          questInfo={SellerStore?.sellerInfo?.questInfo?.timedealQuest}
          openModal={openTimedealQuestModal}
          setOpenModal={setOpenTimedealQuestModal}
          setShowQuest={setShowQuest}
        />
      )
    if (
      !questClearInfo?.beautyQuestClearCondition &&
      (SellerStore.sellerInfo?.permanentMarks?.isBeautySeller ||
        SellerStore.sellerInfo?.representCategoryId === 1)
    )
      return (
        <BeautyQuest
          questInfo={SellerStore?.sellerInfo?.questInfo?.beautyQuest}
          openModal={openBeautyQuestModal}
          setOpenModal={setOpenBeautyQuestModal}
          setShowQuest={setShowQuest}
        />
      )
    return null
  }, [showQuest, openPriceQuestModal, openAdQuestModal, openTimedealQuestModal, openBeautyQuestModal])

  // 타이머 설정 (한 번만)
  useEffect(() => {
    const timer = setTimeout(() => setTimerCheck(true), 120000)
    return () => clearTimeout(timer)
  }, [])

  // 앱 초기화 (한 번만 실행)
  useEffect(() => {
    if (initializationRef.current) return
    initializationRef.current = true

    const initialize = async () => {
      try {
        console.log('Starting app initialization...')
        
        // 1. 토큰 로드 및 SellerInfo 로드 (순차적으로 필요)
        AuthStore.loadToken()
        const sellerResult = await SellerStore.loadSellerInfo()
        
        if (!sellerResult) {
          localStorage.removeItem('@alwayz@seller@token@')
          AuthStore.setToken('')
          history.push('/login')
          return
        }

        // 2. 병렬로 처리 가능한 작업들
        SellerStore.setIsLoading(true)
        
        await Promise.all([
          SellerStore.loadCalculatedSellerPoints(),
          SellerStore.setSellerMaxWeeklyTotalOrdersLastMonth(),
          SellerStore.setSellerRecentTodayDeparturePenalty(),
          SellerStore.loadCalculatedSellerPeriodScores(),
          OrderStore.loadShippingCompanies(),
          OrderStore.loadOrders(),
          ItemStore.loadUnansweredQuestions(),
          SellerStore.loadHolidays(),
          SellerStore.loadBanksInfo(),
          SellerStore.loadDashboardInfo(),
          SellerStore.loadItemsRank(SellerStore.sellerInfo._id),
          ItemStore.loadItemOrderGuarantees(),
          SellerStore.loadOrderForGoodService(),
        ])

        SellerStore.setIsLoading(false)
        setIsSellerInfoPrepared(true)
        setInitializationComplete(true)
        
        console.log('App initialization completed successfully')
      } catch (error) {
        console.error('App initialization failed:', error)
        SellerStore.setIsLoading(false)
        // 에러 처리 로직 추가 가능
      }
    }

    // 소켓 연결 및 초기화 시작
    socket?.connect()
    initialize()
  }, [history])

  // 소켓 초기화 (별도 관리)
  useEffect(() => {
    if (AuthStore.needToConnectSocket && SellerStore.sellerInfo?._id) {
      SellerStore.initializeSocket()
      AuthStore.setNeedToConnectSocket(false)
    }
  }, [AuthStore.needToConnectSocket, SellerStore.sellerInfo?._id])

  // Channel Talk 초기화 (초기화 완료 후)
  useEffect(() => {
    if (initializationComplete && SellerStore.sellerInfo?._id) {
      channelTalkBoot()
    }
  }, [initializationComplete, SellerStore.sellerInfo?._id, channelTalkBoot])

  // 비즈니스 등록 모달 조건 확인
  const shouldShowBusinessRegistrationModal = useMemo(() => {
    if (!isSellerInfoPrepared || !SellerStore.sellerInfo) return false
    if (SellerStore.sellerInfo.sellerName === 'LevitSeller') return false
    if (AuthStore.isMasterAccount) return false
    
    const validation = SellerStore.sellerInfo.dataForValidation
    return (
      validation?.isValidBusinessRegistrationNumberImage !== 'approved' ||
      validation?.isValidBusinessRegistrationNumber !== 'approved' ||
      validation?.isValidMailOrderSalesRegistrationNumber !== 'approved' ||
      validation?.isValidBankAccount !== 'approved'
    )
  }, [isSellerInfoPrepared, SellerStore.sellerInfo, AuthStore.isMasterAccount])

  return (
    <>
      <div className='flex flex-row flex-1 h-full bg-dashboard-background '>
        {SellerStore.isUploading && (
          <ProgressDisplay
            msg={SellerStore?.uploadingStatus?.msg}
            total={SellerStore?.uploadingStatus?.total}
            done={SellerStore?.uploadingStatus?.done}
            success={SellerStore?.uploadingStatus?.success}
            fail={SellerStore?.uploadingStatus?.fail}
            isComplete={SellerStore?.uploadingStatus?.isComplete}
            isAdNudgeOn={SellerStore?.uploadingStatus?.isAdNudgeOn}
            onAdNudgeClick={() => {
              SellerStore.setIsUploading(false)
              if (
                SellerStore?.uploadingStatus?.type === 'oneClick' ||
                SellerStore?.uploadingStatus?.type === 'winnerOneClick'
              ) {
                // 광고대량등록 넛지
                SellerStore.setUploadingStatus({
                  type: '',
                  msg: '',
                  doneMsg: '',
                  total: 0,
                  done: 0,
                  fail: 0,
                })
                history.push(`/ad-apply?batchApplyOn=${true}`)
              }
            }}
            onCompleteCallback={() => {
              SellerStore.setIsUploading(false)

              SellerStore.setUploadingStatus({
                type: '',
                msg: '',
                doneMsg: '',
                total: 0,
                done: 0,
                fail: 0,
              })
            }}
          />
        )}
        <div
          style={{
            position: 'fixed',
            width: 240,
            height: '100%',
            zIndex: 15,
            display: isAgreeToTnCModalOpen ? 'none' : 'block',
          }}
        >
          <Sidebar />
        </div>
        <div className='z-10 flex flex-col grow pl-60'>
          <Header />
          <div className='fixed z-50 right-5 top-20'>
            <div className='flex flex-col items-end'>
              <QuestHandler questClearInfo={questClearInfo} />
            </div>
          </div>
          <div className='flex flex-col items-start px-10 pt-16'>
            <AdminRouter sellerInfo={SellerStore?.sellerInfo} />
          </div>
        </div>

        {/* 사업자 검증 구버전 모달 */}
        {SellerStore?.sellerInfo?.businessRegistrationNumber &&
          !SellerStore.sellerInfo?.isValidBusinessRegistrationNumber &&
          !AuthStore.isMasterAccount && <BusinessRegistrationNumberModal />}

        {/* 사업자 검증 신버전 모달 */}
        {shouldShowBusinessRegistrationModal && <BusinessRegistrationModal />}

        {/* 기존 모달들 */}
        <Modal
          isOpen={OrderStore?.openCancelPreShippingOrderModal}
          onRequestClose={() => {
            OrderStore?.setOpenCancelPreShippingOrderModal(false)
          }}
          style={{
            overlay: {
              zIndex: 20,
            },
            content: {
              left: '20%',
              right: '20%',
              top: '20%',
              bottom: '20%',
              padding: 0,
            },
          }}
        >
          <CancelPreShippingOrderModal
            setOpenModal={OrderStore?.openCancelPreShippingOrderModal}
          />
        </Modal>
        <Modal
          isOpen={OrderStore?.openCancelOrderModal}
          onRequestClose={() => {
            OrderStore?.setOpenCancelOrderModal(false)
          }}
          style={{
            overlay: {
              zIndex: 20,
            },
            content: {
              left: '20%',
              right: '20%',
              top: '20%',
              bottom: '20%',
              padding: 0,
            },
          }}
        >
          <CancelOrderModal />
        </Modal>
        <Modal
          isOpen={OrderStore?.openRevokeRefundModal}
          onRequestClose={() => {
            OrderStore?.setOpenRevokeRefundModal(false)
          }}
          style={{
            overlay: {
              zIndex: 20,
            },
            content: {
              left: '20%',
              right: '20%',
              top: '20%',
              bottom: '20%',
              padding: 0,
            },
          }}
        >
          <RevokeRefundRequestModal />
        </Modal>
        <Modal
          isOpen={OrderStore?.openWithdrawCancelingPreShippingOrderModal}
          onRequestClose={() => {
            OrderStore?.setOpenWithdrawCancelingPreShippingOrderModal(false)
          }}
          style={{
            overlay: {
              zIndex: 20,
            },
            content: {
              left: '20%',
              right: '20%',
              top: '20%',
              bottom: '20%',
              padding: 0,
            },
          }}
        >
          <WithdrawCancelingPreShippingOrderModal
            id='modal'
            setOpenModal={
              OrderStore?.openWithdrawCancelingPreShippingOrderModal
            }
          />
        </Modal>
      </div>
    </>
  )
})

export default AdminScreen
